import { Badge, Flex, Image, Menu, Spacer } from '@chakra-ui/react';
import { useLingui } from '@lingui/react/macro';

import { useAuth } from '#modules/user/features/auth/hooks/useAuth';
import { useIsAuth } from '#modules/user/features/auth/hooks/useIsAuth';
import { useProfile } from '#modules/user/features/profile/hooks/useProfile';
import { Link } from '#features/routing';
import { Button } from '#features/ui';
import { Avatar } from '#features/ui/components/Avatar';
import { IconButton } from '#features/ui/components/IconButton';
import { MenuContent } from '#features/ui/components/Menu';
import { useSSR } from '#hooks/useSSR';

// Same logo asset the LFG bot landing header uses, so the admin console reads as
// the same product surface (reached from the landing's "Dashboard" button) rather
// than the main teamplay.gg app.
const LFG_LOGO_SRC = 'https://media.teamplay.gg/assets/E7ZD/sOE.png';

export const DiscordAdminHeader = () => {
  const { t } = useLingui();
  const { isBrowser } = useSSR();
  const isAuth = useIsAuth();
  const { profile } = useProfile();
  const { logout } = useAuth();

  return (
    <Flex
      as="header"
      h="60px"
      w="100%"
      pos="sticky"
      top="0"
      left="0"
      zIndex="sticky"
      bg="rgba(10, 10, 15, 0.88)"
      backdropFilter="blur(20px) saturate(130%)"
      borderBottomWidth="1px"
      borderColor="rgba(255, 255, 255, 0.08)"
    >
      <Flex maxW="1800px" px={{ base: 3, md: 5 }} py={2} w="100%" flex={1} m="0 auto" align="center">
        <Flex align="center" gap={3} asChild>
          <Link to="/{-$locale}/discord-lfg-bot/admin">
            <Image src={LFG_LOGO_SRC} alt="Teamplay LFG" h="32px" w="auto" />
            <Badge
              colorPalette="purple"
              variant="subtle"
              display={{ base: 'none', sm: 'inline-flex' }}
            >
              {t`Admin`}
            </Badge>
          </Link>
        </Flex>
        <Spacer />
        <Flex align="center" gap={2}>
          <Button asChild variant="ghost" size="sm" display={{ base: 'none', sm: 'inline-flex' }}>
            <Link to="/{-$locale}/discord-lfg-bot">{t`Back to site`}</Link>
          </Button>
          {isBrowser && isAuth ? (
            <Menu.Root positioning={{ strategy: 'fixed', offset: { mainAxis: 20 } }}>
              <Menu.Trigger asChild>
                <IconButton variant="ghost" aria-label={t`Account`} borderRadius="full">
                  <Avatar
                    size="sm"
                    src={profile?.avatar ?? undefined}
                    name={profile?.displayName ?? undefined}
                  />
                </IconButton>
              </Menu.Trigger>
              <MenuContent>
                <Menu.Item value="servers" asChild>
                  <Link to="/{-$locale}/discord-lfg-bot/admin">{t`Your Discord servers`}</Link>
                </Menu.Item>
                <Menu.Separator />
                <Menu.Item value="logout" onClick={() => logout()}>
                  {t`common:auth.logOut`}
                </Menu.Item>
              </MenuContent>
            </Menu.Root>
          ) : null}
        </Flex>
      </Flex>
    </Flex>
  );
};
